import { flow, pipe } from 'fp-ts/lib/function';
import { EntityItem } from '@/entities/entityItem';
import { chain, map } from 'fp-ts/lib/TaskEither';
import { detailCodec, formCodec, listCodec } from './codec';
import {
  addVersionForUpdate,
  authHeaders,
  createAuthorizedDeleteRequest,
  createAuthorizedGetRequest,
  createAuthorizedPostRequest,
  createAuthorizedPutRequest,
  createPostRequest,
  decode,
  decodeId,
  decodeVersioned,
  fetchApi,
  fetchUnparsedApi,
  fileToFormData,
  formMultipartHeadersWithJson,
  setBody,
  setBodyFormData,
  setQuery,
  Versioned,
} from '../..';
import { encodeEntityItemQuery, EntityItemQuery } from '@/services/api/resources/entityItem/query';
import { filterCodec } from '@/services/api/resources/filter';
import { resourceBulkDelete } from '@/services/api/resources/bulkDelete';
import * as q from '@/services/api/request/query';

const baseUrl = '/v2/entity-items';
const filterUrl = '/v2/filters/entity-items';
const recordUrl = (id: number): string => `${baseUrl}/${id}`;
const importUrl = `${baseUrl}/import`;
const exportUrl = `${baseUrl}/export`;

const addForTypeQuery = (query?: { forType?: number }) =>
  map((request: Request) => query?.forType ? setQuery({ forType: q.number(query.forType) })(request) : request);

export const list = (query: EntityItemQuery) => pipe(
  baseUrl,
  createAuthorizedGetRequest,
  map(setQuery(encodeEntityItemQuery(query))),
  chain(fetchApi),
  chain(decode(listCodec)),
);

export const filter = () => pipe(
  filterUrl,
  createAuthorizedGetRequest,
  chain(fetchApi),
  chain(decode(filterCodec)),
);

export const create = (entityItem: EntityItem) => pipe(
  baseUrl,
  createAuthorizedPostRequest,
  map(setBody(formCodec.encode(entityItem))),
  chain(fetchApi),
  chain(decodeId),
);

export const get = flow(
  recordUrl,
  createAuthorizedGetRequest,
  chain(fetchApi),
  chain(decodeVersioned(detailCodec)),
);

export const update = (id: number, entityItem: Versioned<EntityItem>) => pipe(
  id,
  recordUrl,
  createAuthorizedPutRequest,
  map(addVersionForUpdate(entityItem.version)),
  map(setBody(formCodec.encode(entityItem.data))),
  chain(fetchApi),
);

export const remove = flow(
  recordUrl,
  createAuthorizedDeleteRequest,
  chain(fetchApi),
);

export const bulkDelete = (records: number[]) => resourceBulkDelete(records, baseUrl);

export const upload = (file: File, query?: { forType?: number }) => pipe(
  importUrl,
  authHeaders(formMultipartHeadersWithJson(createPostRequest)),
  addForTypeQuery(query),
  map(setBodyFormData(fileToFormData(file))),
  chain(fetchApi),
);

export const download = (query?: { forType?: number }) => pipe(
  exportUrl,
  createAuthorizedGetRequest,
  addForTypeQuery(query),
  chain(fetchUnparsedApi),
);
